import { useState } from "react";
import { Navigate, useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ASSETS, formatApiError } from "@/lib/api";
import { Loader2, Eye, EyeOff, LogIn, UserPlus, ArrowRight } from "lucide-react";

export default function StaffLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next")?.startsWith("/") ? params.get("next") : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  if (user) {
    if (next) return <Navigate to={next} replace />;
    if (user.role === "staff") return <Navigate to="/staff" replace />;
    return <Navigate to="/admin" replace />;
  }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    try {
      const u = await login(email, password);
      if (next) navigate(next);
      else if (u.role === "staff") navigate("/staff");
      else navigate("/admin");
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Login failed");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--trio-bg)] text-black flex flex-col">
      {/* Hero header */}
      <header className="bg-[var(--trio-text)] text-[var(--trio-bg)] pt-10 pb-8 sm:pt-14 sm:pb-12 px-6 text-center">
        <img src={ASSETS.logo} alt="Trio" className="h-16 sm:h-20 w-auto mx-auto mb-4 invert" />
        <div className="text-[11px] tracking-[0.35em] uppercase text-[var(--trio-accent)] font-bold">
          Employee Portal
        </div>
        <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tight mt-2">
          Welcome, Crew.
        </h1>
        <p className="text-sm text-[var(--trio-bg)]/70 mt-3 max-w-md mx-auto">
          Manage your schedule, availability, referral link, and commissions — all in one spot.
        </p>
      </header>

      {/* Main CTA card */}
      <main className="flex-1 flex flex-col items-center justify-start px-4 sm:px-6 -mt-6 pb-10">
        <div className="w-full max-w-md bg-white border border-black/10 shadow-2xl rounded-lg p-6 sm:p-8">
          <form onSubmit={submit} className="space-y-4" data-testid="staff-login-form">
            <div>
              <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">Email</label>
              <input
                type="email"
                required
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="staff-login-email"
                className="w-full mt-2 bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-4 text-base text-black placeholder-black/40 focus:outline-none rounded"
              />
            </div>
            <div>
              <label className="text-xs tracking-widest uppercase text-black/70 font-semibold">Password</label>
              <div className="relative mt-2">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="staff-login-password"
                  className="w-full bg-white border border-black/25 focus:border-[var(--trio-primary)] px-4 py-4 pr-12 text-base text-black placeholder-black/40 focus:outline-none rounded"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  tabIndex={-1}
                  data-testid="staff-login-toggle-visibility"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-black/40 hover:text-black/70 p-1"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="mt-2 text-right">
                <Link
                  to="/forgot-password"
                  data-testid="staff-forgot-link"
                  className="text-[11px] tracking-widest uppercase text-black/60 hover:text-[var(--trio-primary)]"
                >
                  Forgot password?
                </Link>
              </div>
            </div>
            {err && (
              <div data-testid="staff-login-error" className="text-sm text-red-600 font-medium bg-red-50 border border-red-200 px-3 py-2 rounded">
                {err}
              </div>
            )}

            {/* Big primary CTA */}
            <button
              type="submit"
              disabled={submitting}
              data-testid="staff-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-[var(--trio-primary)] hover:bg-[var(--trio-primary-hover)] text-white py-5 text-base tracking-[0.25em] uppercase font-bold disabled:opacity-60 rounded shadow-lg shadow-[var(--trio-primary)]/25 active:scale-[0.98] transition-transform"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-black/40 font-bold">
            <div className="flex-1 h-px bg-black/10" />
            New Here?
            <div className="flex-1 h-px bg-black/10" />
          </div>

          {/* Big secondary CTA — Join the Crew */}
          <Link
            to="/staff/signup"
            data-testid="staff-signup-link"
            className="w-full inline-flex items-center justify-between gap-3 border-2 border-[var(--trio-text)] hover:bg-[var(--trio-text)] hover:text-[var(--trio-bg)] text-black py-5 px-5 text-base tracking-[0.25em] uppercase font-bold rounded transition-colors group"
          >
            <span className="flex items-center gap-3">
              <UserPlus className="w-5 h-5" />
              Join the Crew
            </span>
            <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="text-[11px] text-black/50 mt-2 text-center">
            Ask the owner for the current invite code.
          </p>
        </div>

        {/* Owner footer */}
        <div className="mt-8 text-[11px] tracking-widest uppercase text-black/50 text-center">
          Owner?{" "}
          <Link to="/admin/login" className="underline hover:text-[var(--trio-primary)]">
            Sign in there
          </Link>
        </div>
      </main>
    </div>
  );
}
